// LiveClassRoom.tsx
import React, { useEffect, useRef, useState, useCallback, useMemo, memo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  HMSRoomProvider,
  useHMSActions,
  useHMSStore,
  selectIsConnectedToRoom,
  selectRoomState,
  selectPeers,
  selectLocalPeer,
  selectIsLocalAudioEnabled,
  selectIsLocalVideoEnabled,
  selectIsLocalScreenShared,
  selectPeerScreenSharing,
  selectScreenShareByPeerID,
  selectHMSMessages,
  selectUnreadHMSMessagesCount,
  HMSRoomState,
  HMSPeer,
} from "@100mslive/react-sdk";
import "./LiveClassRoom.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001/api/v1";
const HOST_ROLE = "broadcaster"; // teacher role name in 100ms dashboard

/* ============================================================
   ICONS — clean inline SVGs (no external dependencies)
   ============================================================ */
const Icon = {
  MicOn: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  ),
  MicOff: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V5a3 3 0 0 0-5.94-.6" />
      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  ),
  VideoOn: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  ),
  VideoOff: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ),
  Screen: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
      <polyline points="8 10 12 6 16 10" />
      <line x1="12" y1="6" x2="12" y2="14" />
    </svg>
  ),
  Chat: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  Hand: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0" />
      <path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2" />
      <path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8" />
      <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
    </svg>
  ),
  PhoneOff: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
      <line x1="23" y1="1" x2="1" y2="23" />
    </svg>
  ),
  Close: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Back: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  Send: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  ),
  Users: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
};

/* ============================================================
   HAND RAISE — uses peer metadata so all peers can see it
   ============================================================ */
const parseMetadata = (meta?: string): { isHandRaised?: boolean } => {
  if (!meta) return {};
  try { return JSON.parse(meta); } catch { return {}; }
};

/* ============================================================
   JOINER — StrictMode-safe
   ============================================================ */
interface HmsJoinerProps {
  token: string;
  userName: string;
  onError: (msg: string) => void;
}
const HmsJoiner: React.FC<HmsJoinerProps> = ({ token, userName, onError }) => {
  const hmsActions = useHMSActions();
  const roomState = useHMSStore(selectRoomState);
  const joinAttempted = useRef(false);

  useEffect(() => {
    if (!token || !userName) return;
    if (joinAttempted.current) return;
    if (
      roomState === HMSRoomState.Connected ||
      roomState === HMSRoomState.Connecting ||
      roomState === HMSRoomState.Reconnecting
    ) return;

    joinAttempted.current = true;
    console.log("🟢 Joining as:", userName);

    hmsActions
      .join({
        authToken: token,
        userName,
        settings: { isAudioMuted: false, isVideoMuted: false },
      })
      .then(() => console.log("✅ JOIN SUCCESS"))
      .catch((e) => {
        console.error("❌ JOIN FAILED:", e);
        joinAttempted.current = false;
        onError(e?.description || e?.message || String(e) || "Unknown join error");
      });
  }, [token, userName, hmsActions, roomState, onError]);

  return null;
};

/* ============================================================
   BACK BUTTON
   ============================================================ */
const BackButton: React.FC<{ onClick: () => void; label?: string }> = ({ onClick, label = "Back" }) => (
  <button className="back-btn" onClick={onClick} title="Leave class and go back">
    <Icon.Back />
    <span>{label}</span>
  </button>
);

/* ============================================================
   VIDEO TILE — memoized for 100+ peers performance
   ============================================================ */
interface VideoTileProps {
  peer: HMSPeer;
  isHost?: boolean;
  isMain?: boolean;
  trackId?: string;
}
const VideoTileInner: React.FC<VideoTileProps> = ({ peer, isHost, isMain, trackId }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hmsActions = useHMSActions();
  const useTrackId = trackId ?? peer.videoTrack;
  const meta = useMemo(() => parseMetadata(peer.metadata), [peer.metadata]);

  useEffect(() => {
    const el = videoRef.current;
    if (useTrackId && el) {
      hmsActions.attachVideo(useTrackId, el);
      return () => { hmsActions.detachVideo(useTrackId, el).catch(() => {}); };
    }
  }, [useTrackId, hmsActions]);

  const tileClass = `tile ${isMain ? "main" : "thumb"} ${isHost ? "host" : ""}`;
  const initials = (peer.name || "?").charAt(0).toUpperCase();

  return (
    <div className={tileClass}>
      {useTrackId ? (
        <video ref={videoRef} autoPlay playsInline muted={peer.isLocal} />
      ) : (
        <div className="avatar">{initials}</div>
      )}

      {meta.isHandRaised && (
        <div className="hand-raised-badge" title={`${peer.name} raised hand`}>
          ✋
        </div>
      )}

      <div className="name">
        <span className="name-text">
          {peer.name}
          {peer.isLocal && " (You)"}
        </span>
        {isHost && !trackId && <span className="name-tag">Teacher</span>}
        {trackId && <span className="name-tag green">Sharing</span>}
      </div>
    </div>
  );
};
const VideoTile = memo(VideoTileInner, (prev, next) =>
  prev.peer.id === next.peer.id &&
  prev.peer.videoTrack === next.peer.videoTrack &&
  prev.peer.metadata === next.peer.metadata &&
  prev.peer.name === next.peer.name &&
  prev.trackId === next.trackId &&
  prev.isMain === next.isMain &&
  prev.isHost === next.isHost
);

/* ============================================================
   CHAT PANEL — full-screen on mobile, side panel on desktop
   ============================================================ */
const ChatPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const hmsActions = useHMSActions();
  const messages = useHMSStore(selectHMSMessages);
  const localPeer = useHMSStore(selectLocalPeer);
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  useEffect(() => {
    // Auto-focus input when panel opens (desktop only — mobile would pop keyboard)
    if (window.innerWidth > 900) inputRef.current?.focus();
  }, []);

  const send = () => {
    const txt = input.trim();
    if (!txt) return;
    hmsActions.sendBroadcastMessage(txt);
    setInput("");
  };

  return (
    <>
      <div className="chat-backdrop" onClick={onClose} />
      <aside className="chat-panel" role="dialog" aria-label="Class chat">
        <header className="chat-header">
          <button
            className="chat-close-btn"
            onClick={onClose}
            aria-label="Close chat"
            title="Close chat"
          >
            <Icon.Close />
            <span>Close</span>
          </button>
          <div className="chat-title">
            <h3>Class Chat</h3>
            <p className="chat-subtitle">Visible to everyone</p>
          </div>
        </header>

        <div className="chat-messages" ref={scrollRef}>
          {messages.length === 0 ? (
            <div className="chat-empty">
              <div className="chat-empty-icon">💬</div>
              <p>No messages yet</p>
              <span>Be the first to say hello 👋</span>
            </div>
          ) : (
            messages.map((m) => {
              const isOwn = m.sender === localPeer?.id;
              return (
                <div key={m.id} className={`chat-msg ${isOwn ? "own" : ""}`}>
                  {!isOwn && <span className="chat-sender">{m.senderName}</span>}
                  <span className="chat-bubble">{m.message}</span>
                </div>
              );
            })
          )}
        </div>

        <div className="chat-input">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Type a message..."
            maxLength={500}
          />
          <button onClick={send} aria-label="Send" disabled={!input.trim()}>
            <Icon.Send />
          </button>
        </div>
      </aside>
    </>
  );
};

/* ============================================================
   CONTROLS — bottom floating bar
   ============================================================ */
interface ControlsProps {
  isHost: boolean;
  onLeave: () => void;
  onToggleChat: () => void;
  chatOpen: boolean;
  unreadCount: number;
  isHandRaised: boolean;
  onToggleHand: () => void;
  handRaisedCount: number;
}
const Controls: React.FC<ControlsProps> = ({
  isHost, onLeave, onToggleChat, chatOpen, unreadCount,
  isHandRaised, onToggleHand, handRaisedCount,
}) => {
  const hmsActions = useHMSActions();
  const isAudioOn = useHMSStore(selectIsLocalAudioEnabled);
  const isVideoOn = useHMSStore(selectIsLocalVideoEnabled);
  const isScreenShared = useHMSStore(selectIsLocalScreenShared);

  const toggleAudio = () => hmsActions.setLocalAudioEnabled(!isAudioOn);
  const toggleVideo = () => hmsActions.setLocalVideoEnabled(!isVideoOn);
  const toggleShare = async () => {
    try { await hmsActions.setScreenShareEnabled(!isScreenShared); }
    catch (e) { console.error("Screen share error:", e); }
  };

  return (
    <div className="controls">
      <button
        className={`ctrl ${isAudioOn ? "" : "off"}`}
        onClick={toggleAudio}
        title={isAudioOn ? "Mute" : "Unmute"}
        aria-label={isAudioOn ? "Mute microphone" : "Unmute microphone"}
      >
        {isAudioOn ? <Icon.MicOn /> : <Icon.MicOff />}
      </button>

      <button
        className={`ctrl ${isVideoOn ? "" : "off"}`}
        onClick={toggleVideo}
        title={isVideoOn ? "Turn off camera" : "Turn on camera"}
        aria-label={isVideoOn ? "Turn off camera" : "Turn on camera"}
      >
        {isVideoOn ? <Icon.VideoOn /> : <Icon.VideoOff />}
      </button>

      {isHost && (
        <button
          className={`ctrl ${isScreenShared ? "active" : ""}`}
          onClick={toggleShare}
          title={isScreenShared ? "Stop sharing" : "Share screen"}
          aria-label="Share screen"
        >
          <Icon.Screen />
        </button>
      )}

      {!isHost && (
        <button
          className={`ctrl ${isHandRaised ? "active" : ""}`}
          onClick={onToggleHand}
          title={isHandRaised ? "Lower hand" : "Raise hand"}
          aria-label="Raise hand"
        >
          <Icon.Hand />
        </button>
      )}

      <button
        className={`ctrl ${chatOpen ? "active" : ""}`}
        onClick={onToggleChat}
        title="Chat"
        aria-label="Open chat"
      >
        <Icon.Chat />
        {unreadCount > 0 && !chatOpen && (
          <span className="badge">{unreadCount > 9 ? "9+" : unreadCount}</span>
        )}
      </button>

      {isHost && handRaisedCount > 0 && (
        <div className="hand-indicator" title={`${handRaisedCount} student${handRaisedCount > 1 ? "s" : ""} raised hand`}>
          ✋ {handRaisedCount}
        </div>
      )}

      <button className="ctrl leave" onClick={onLeave} aria-label="Leave class">
        <Icon.PhoneOff />
        <span className="leave-label">Leave</span>
      </button>
    </div>
  );
};

/* ============================================================
   JOINING SCREEN
   ============================================================ */
interface JoiningScreenProps {
  roomState: HMSRoomState;
  joinError: string;
  onBack: () => void;
}
const JoiningScreen: React.FC<JoiningScreenProps> = ({ roomState, joinError, onBack }) => {
  const [tookTooLong, setTookTooLong] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setTookTooLong(true), 15000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="room joining">
      <header className="topbar topbar-floating">
        <BackButton onClick={onBack} />
      </header>
      {joinError ? (
        <div className="joining-card error">
          <div className="error-icon">⚠️</div>
          <h3>Couldn't connect to the class</h3>
          <p className="err-detail">{joinError}</p>
          <button onClick={() => window.location.reload()}>Try again</button>
        </div>
      ) : (
        <div className="joining-card">
          <div className="spinner" />
          <p>Joining live class…</p>
          <p className="state-tag">Status: <code>{roomState}</code></p>
          {tookTooLong && (
            <div className="timeout-warn">
              <p><strong>Still trying after 15s.</strong></p>
              <p>Check browser console (F12) for details.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ============================================================
   LEAVE CONFIRMATION MODAL — matches 100ms native style
   ============================================================ */
interface LeaveConfirmModalProps {
  onCancel: () => void;
  onConfirm: () => void;
}
const LeaveConfirmModal: React.FC<LeaveConfirmModalProps> = ({ onCancel, onConfirm }) => {
  // Close on Escape key
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <>
      <div className="leave-backdrop" onClick={onCancel} />
      <div className="leave-modal" role="dialog" aria-modal="true" aria-labelledby="leave-title">
        <div className="leave-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <h2 id="leave-title" className="leave-title">Leave class?</h2>
        <p className="leave-message">
          Others will continue after you leave. You can join the session again.
        </p>
        <div className="leave-actions">
          <button className="leave-btn cancel" onClick={onCancel} autoFocus>
            Cancel
          </button>
          <button className="leave-btn confirm" onClick={onConfirm}>
            Leave Session
          </button>
        </div>
      </div>
    </>
  );
};

/* ============================================================
   CLASSROOM — main view
   ============================================================ */
interface ClassroomProps {
  onLeave: () => Promise<void>;
}
const Classroom: React.FC<ClassroomProps> = ({ onLeave }) => {
  const hmsActions = useHMSActions();
  const peers = useHMSStore(selectPeers);
  const localPeer = useHMSStore(selectLocalPeer);
  const presenter = useHMSStore(selectPeerScreenSharing);
  const screenShareTrack = useHMSStore(selectScreenShareByPeerID(presenter?.id || ""));
  const screenShareTrackId = screenShareTrack?.id;
  const unreadCount = useHMSStore(selectUnreadHMSMessagesCount);

  const [chatOpen, setChatOpen] = useState(false);
  const [showAllParticipants, setShowAllParticipants] = useState(false);

  const teacher = useMemo(() => peers.find((p) => p.roleName === HOST_ROLE), [peers]);
  const isLocalHost = localPeer?.roleName === HOST_ROLE;
  const showingScreenShare = Boolean(presenter && screenShareTrackId);

  // Hand raise: read local peer's own metadata
  const localMeta = useMemo(() => parseMetadata(localPeer?.metadata), [localPeer?.metadata]);
  const isHandRaised = Boolean(localMeta.isHandRaised);

  const toggleHand = useCallback(async () => {
    try {
      const next = { ...localMeta, isHandRaised: !isHandRaised };
      await hmsActions.changeMetadata(JSON.stringify(next));
    } catch (e) {
      console.error("Hand raise failed:", e);
    }
  }, [hmsActions, isHandRaised, localMeta]);

  // Hand-raised peers (excluding self for the count)
  const handRaisedPeers = useMemo(
    () => peers.filter((p) => parseMetadata(p.metadata).isHandRaised && !p.isLocal),
    [peers]
  );

  // Build the side strip:
  // Priority 1: hand-raised peers, Priority 2: video-on peers, Priority 3: rest
  // For 100+ peers, only show first ~20; rest accessible via "View all"
  const stripPeers = useMemo(() => {
    const others = peers.filter((p) => {
      if (!teacher && !showingScreenShare && p.isLocal) return false;
      if (teacher && p.id === teacher.id && !showingScreenShare) return false;
      return true;
    });

    return [...others].sort((a, b) => {
      const aHand = parseMetadata(a.metadata).isHandRaised ? 1 : 0;
      const bHand = parseMetadata(b.metadata).isHandRaised ? 1 : 0;
      if (aHand !== bHand) return bHand - aHand;
      const aVid = a.videoTrack ? 1 : 0;
      const bVid = b.videoTrack ? 1 : 0;
      if (aVid !== bVid) return bVid - aVid;
      return 0;
    });
  }, [peers, teacher, showingScreenShare]);

  const MAX_STRIP = 12;
  const visibleStrip = stripPeers.slice(0, MAX_STRIP);
  const hiddenCount = Math.max(0, stripPeers.length - MAX_STRIP);

  return (
    <div className="room">
      <header className="topbar">
        <div className="topbar-left">
          <BackButton onClick={onLeave} />
          <div className="brand">
            <span className="dot-live" />
            <strong>Acadebility Live</strong>
          </div>
        </div>
        <div className="topbar-right">
          <button
            className="participants-btn"
            onClick={() => setShowAllParticipants(true)}
            title="View all participants"
          >
            <Icon.Users />
            <span>{peers.length}</span>
          </button>
        </div>
      </header>

      <main className={`stage ${chatOpen ? "with-chat" : ""}`}>
        <section className="main-view">
          {showingScreenShare && presenter ? (
            <VideoTile peer={presenter} isMain trackId={screenShareTrackId} />
          ) : teacher ? (
            <VideoTile peer={teacher} isHost isMain />
          ) : localPeer ? (
            <>
              <VideoTile peer={localPeer} isMain isHost={isLocalHost} />
              <div className="waiting-banner">
                {isLocalHost ? (
                  <><span className="dot-live" />You're live. Students will see you when they join.</>
                ) : (
                  <><div className="banner-spinner" />Waiting for the teacher to join…</>
                )}
              </div>
            </>
          ) : (
            <div className="waiting">
              <div className="waiting-icon">⏳</div>
              <h2>Connecting…</h2>
            </div>
          )}
        </section>

        <aside className="strip">
          {showingScreenShare && teacher && (
            <VideoTile key={`host-${teacher.id}`} peer={teacher} isHost />
          )}
          {visibleStrip.map((p) => (
            <VideoTile
              key={p.id}
              peer={p}
              isHost={p.roleName === HOST_ROLE}
            />
          ))}
          {hiddenCount > 0 && (
            <button className="strip-more" onClick={() => setShowAllParticipants(true)}>
              <Icon.Users />
              <span>+{hiddenCount} more</span>
            </button>
          )}
          {visibleStrip.length === 0 && !showingScreenShare && (
            <div className="strip-empty">No one else in class yet</div>
          )}
        </aside>
      </main>

      <Controls
        isHost={isLocalHost}
        onLeave={onLeave}
        onToggleChat={() => setChatOpen((v) => !v)}
        chatOpen={chatOpen}
        unreadCount={unreadCount}
        isHandRaised={isHandRaised}
        onToggleHand={toggleHand}
        handRaisedCount={handRaisedPeers.length}
      />

      {chatOpen && <ChatPanel onClose={() => setChatOpen(false)} />}
      {showAllParticipants && (
        <ParticipantsModal
          peers={peers}
          onClose={() => setShowAllParticipants(false)}
        />
      )}
    </div>
  );
};

/* ============================================================
   PARTICIPANTS MODAL — full list for large classes
   ============================================================ */
const ParticipantsModal: React.FC<{ peers: HMSPeer[]; onClose: () => void }> = ({ peers, onClose }) => {
  const [query, setQuery] = useState("");
  const filtered = peers.filter((p) =>
    p.name.toLowerCase().includes(query.toLowerCase())
  );
  const teachers = filtered.filter((p) => p.roleName === HOST_ROLE);
  const students = filtered.filter((p) => p.roleName !== HOST_ROLE);

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="participants-modal" role="dialog">
        <header className="participants-header">
          <div>
            <h3>Participants</h3>
            <p>{peers.length} in class</p>
          </div>
          <button className="chat-close" onClick={onClose} aria-label="Close">
            <Icon.Close />
          </button>
        </header>

        <div className="participants-search">
          <input
            placeholder="Search by name..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="participants-list">
          {teachers.length > 0 && (
            <div className="participants-section">
              <h4>Teacher</h4>
              {teachers.map((p) => (
                <PeerRow key={p.id} peer={p} isHost />
              ))}
            </div>
          )}
          {students.length > 0 && (
            <div className="participants-section">
              <h4>Students ({students.length})</h4>
              {students.map((p) => <PeerRow key={p.id} peer={p} />)}
            </div>
          )}
          {filtered.length === 0 && (
            <p className="participants-empty">No participants match "{query}"</p>
          )}
        </div>
      </div>
    </>
  );
};

const PeerRow: React.FC<{ peer: HMSPeer; isHost?: boolean }> = ({ peer, isHost }) => {
  const meta = parseMetadata(peer.metadata);
  return (
    <div className="peer-row">
      <div className="peer-avatar">{(peer.name || "?").charAt(0).toUpperCase()}</div>
      <div className="peer-info">
        <span className="peer-name">
          {peer.name}{peer.isLocal && " (You)"}
        </span>
        <span className="peer-meta">
          {isHost ? "Teacher" : "Student"}
          {peer.audioTrack ? "" : " · muted"}
        </span>
      </div>
      {meta.isHandRaised && <span className="peer-hand">✋</span>}
    </div>
  );
};

/* ============================================================
   INNER — lives inside HMSRoomProvider
   ============================================================ */
interface InnerProps {
  token: string;
  userName: string;
  loading: boolean;
  error: string;
  onBack: () => void;
}
const Inner: React.FC<InnerProps> = ({ token, userName, loading, error, onBack }) => {
  const hmsActions = useHMSActions();
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const roomState = useHMSStore(selectRoomState);
  const [joinError, setJoinError] = useState("");

  // 👇 BULLETPROOF camera release.
  // The 100ms SDK creates MediaStreamTracks that survive `leave()` if anything
  // still holds a reference. We must explicitly stop every track from every
  // <video> AND <audio> element, AND scan for any active getUserMedia streams
  // by enumerating navigator.mediaDevices for active inputs.
  const doFullCleanup = useCallback(async () => {
    // Step 1: Stop tracks on every media element on the page
    const stopMediaElementTracks = () => {
      const elements = document.querySelectorAll("video, audio");
      elements.forEach((el) => {
        try {
          const media = el as HTMLMediaElement;
          const stream = media.srcObject as MediaStream | null;
          if (stream && typeof stream.getTracks === "function") {
            stream.getTracks().forEach((t) => {
              try { t.stop(); } catch {}
            });
          }
          media.srcObject = null;
          media.pause();
          media.removeAttribute("src");
          media.load();
        } catch {}
      });
    };

    // Step 2: Disable through SDK first (signals 100ms to release)
    try { await hmsActions.setScreenShareEnabled(false); } catch {}
    try { await hmsActions.setLocalAudioEnabled(false); } catch {}
    try { await hmsActions.setLocalVideoEnabled(false); } catch {}

    // Step 3: Stop tracks on DOM elements
    stopMediaElementTracks();

    // Step 4: Leave room
    try { await hmsActions.leave(); } catch (e) { console.warn("leave():", e); }

    // Step 5: Stop tracks AGAIN after leave (catches anything SDK created during teardown)
    stopMediaElementTracks();

    // Step 6: Final nuclear option — briefly grab and release the camera/mic
    // so the browser forcibly recognises devices as free.
    try {
      const dummyStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      }).catch(() => null);
      if (dummyStream) {
        dummyStream.getTracks().forEach((t) => {
          try { t.stop(); } catch {}
        });
      }
    } catch {}
  }, [hmsActions]);

  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Called by Leave/Back buttons — opens the confirmation modal
  const handleLeave = useCallback(async () => {
    setShowLeaveConfirm(true);
  }, []);

  // Called when user confirms in the modal
  const handleConfirmLeave = useCallback(async () => {
    setShowLeaveConfirm(false);
    await doFullCleanup();
    onBack();
  }, [doFullCleanup, onBack]);

  useEffect(() => {
    const onUnload = () => {
      // Aggressive cleanup on tab close
      try {
        document.querySelectorAll("video, audio").forEach((el) => {
          const m = el as HTMLMediaElement;
          const s = m.srcObject as MediaStream | null;
          if (s?.getTracks) s.getTracks().forEach((t) => t.stop());
          m.srcObject = null;
        });
      } catch {}
      try { hmsActions.leave(); } catch {}
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [hmsActions]);

  if (loading) {
    return (
      <div className="page-state">
        <header className="topbar topbar-floating">
          <BackButton onClick={onBack} />
        </header>
        <div className="spinner" />
        <p>Loading class…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-state error">
        <header className="topbar topbar-floating">
          <BackButton onClick={onBack} />
        </header>
        <h3>Couldn't join class</h3>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try again</button>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <>
        <HmsJoiner token={token} userName={userName} onError={setJoinError} />
        <JoiningScreen roomState={roomState} joinError={joinError} onBack={handleLeave} />
        {showLeaveConfirm && (
          <LeaveConfirmModal
            onCancel={() => setShowLeaveConfirm(false)}
            onConfirm={handleConfirmLeave}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Classroom onLeave={handleLeave} />
      {showLeaveConfirm && (
        <LeaveConfirmModal
          onCancel={() => setShowLeaveConfirm(false)}
          onConfirm={handleConfirmLeave}
        />
      )}
    </>
  );
};

/* ============================================================
   ROOT
   ============================================================ */
const LiveClassRoom: React.FC = () => {
  const { classId, course_slug } = useParams();
  const navigate = useNavigate();

  const [token, setToken] = useState("");
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!localStorage.getItem("token")) navigate("/");
  }, [navigate]);

  useEffect(() => {
    if (!classId) return;
    let cancelled = false;
    console.log("📡 Fetching HMS token for classId:", classId);

    fetch(`${API_BASE}/live-classes/${classId}/hms-token`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
    })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        console.log("📥 TOKEN RESPONSE:", d);
        if (!d.success) { setError(d.message || "Could not fetch class token"); return; }
        if (!d.data?.token) { setError("Backend returned no token"); return; }
        setToken(d.data.token);
        setUserName(d.data.userName || "Student");
      })
      .catch((e) => {
        if (cancelled) return;
        console.error("Token fetch error:", e);
        setError("Network error — please check your connection");
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [classId]);

  const handleBack = useCallback(() => {
    // 👇 HARD reload instead of SPA navigation.
    // React Router's navigate() keeps the page alive — any lingering MediaStream
    // references survive. A full page reload destroys the entire JS context,
    // which forces the browser to release the camera/mic at the OS level.
    const target = course_slug ? `/live-classes/${course_slug}` : "/";
    window.location.href = target;
  }, [course_slug]);

  return (
    <HMSRoomProvider>
      <Inner
        token={token}
        userName={userName}
        loading={loading}
        error={error}
        onBack={handleBack}
      />
    </HMSRoomProvider>
  );
};

export default LiveClassRoom;
